import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../contexts/AuthContext";
import { GraduationCap, MessageSquare, Bot, Users, Calendar, ArrowRight, BookOpen, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatTile = ({ icon: Icon, label, value, color = "text-primary" }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-semibold">{value}</p>
        <p className="text-xs text-muted-foreground uppercase tracking-widest">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const QuickAction = ({ to, icon: Icon, title, description }) => (
  <Link to={to} className="block group">
    <Card className="bg-card border-border hover:border-primary/40 transition-colors">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-sm bg-primary/15 flex items-center justify-center text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </CardContent>
    </Card>
  </Link>
);

const DozentDashboard = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [participantsCount, setParticipantsCount] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, partsRes] = await Promise.all([
        axios.get(`${API}/courses`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
      ]);
      setCourses(coursesRes.data || []);
      const participants = (partsRes.data || []).filter((u) => u.role === "participant");
      setParticipantsCount(participants.length);
    } catch (err) {
      // silent
    }
  };

  const allowed = user?.allowed_areas || [];
  const hasArea = (a) => user?.role === "super_admin" || allowed.includes(a);

  return (
    <AdminLayout title={`Willkommen, ${user?.name?.split(" ")[0] || "Dozent"}`}>
      <div className="space-y-6" data-testid="dozent-dashboard">
        {/* Hero */}
        <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/30">
          <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-5 w-5 text-primary" />
                <Badge variant="outline" className="text-xs">Dozenten-Portal</Badge>
              </div>
              <p className="text-lg font-semibold">Schön, dass du da bist!</p>
              <p className="text-sm text-muted-foreground">
                Hier findest du alle Werkzeuge für deinen Unterricht – inkl. KI-Assistent für didaktische Tipps.
              </p>
            </div>
            <Link to="/admin/ai-assistent">
              <Button className="gap-2" data-testid="dozent-open-ai-btn">
                <Bot className="h-4 w-4" />
                KI-Assistent öffnen
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={BookOpen} label="Kurse" value={courses.length} />
          <StatTile icon={Users} label="Teilnehmer" value={participantsCount ?? "—"} color="text-blue-500" />
          <StatTile icon={Calendar} label="Heute" value={new Date().toLocaleDateString("de-DE", { day: "numeric", month: "short" })} color="text-yellow-500" />
          <StatTile icon={GraduationCap} label="Rolle" value="Dozent" color="text-green-500" />
        </div>

        {/* Quick Actions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base">Schnellzugriff</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {hasArea("courses") && (
              <QuickAction to="/admin/courses" icon={GraduationCap} title="Kursverwaltung" description="Kurse pflegen & Inhalte hochladen" />
            )}
            {hasArea("messages") && (
              <QuickAction to="/admin/messages" icon={MessageSquare} title="Nachrichten" description="Teilnehmer kontaktieren" />
            )}
            {hasArea("participants") && (
              <QuickAction to="/admin/participants" icon={Users} title="Teilnehmer" description="Übersicht aller Lernenden" />
            )}
            <QuickAction to="/admin/ai-assistent" icon={Bot} title="KI-Assistent" description="Didaktische Tipps & Erklärungen" />
          </CardContent>
        </Card>

        {/* My Courses */}
        {courses.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base">Meine Kurse ({courses.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {courses.slice(0, 5).map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-sm bg-secondary/30 border border-border">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{c.title || c.name}</p>
                    {c.description && (
                      <p className="text-xs text-muted-foreground truncate">{c.description}</p>
                    )}
                  </div>
                  <Link to={`/admin/courses`}>
                    <Button size="sm" variant="ghost" className="gap-1 text-primary">
                      Öffnen <ArrowRight className="h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default DozentDashboard;
